/**
 * IssueCountBadge - Compact per-article issue count indicator.
 *
 * Renders a small pill (or calm teal check at 0 issues) summarising the
 * precomputed issue count attached to list-shaped content responses.
 *
 * States:
 *  - count == null (or undefined): nothing rendered (count not computable).
 *  - count.total === 0: calm teal CheckCircle (clean).
 *  - count.blocking > 0: danger pill showing the total count.
 *  - count.blocking === 0 && count.review > 0: warning pill showing the total.
 *
 * Mirrors the shape produced by the backend `issue_count` field on the
 * ContentSummary / CalendarItem / Content list responses:
 *   `{ total: number, blocking: number, review: number } | null`.
 *
 * Follows the QAStatusBadge pattern (compact table-cell mode + full Tag mode),
 * but uses ONLY theme-token CSS variables (no Tailwind color classes) so the
 * badge stays visually consistent with the rest of the content-planner row.
 */

import React from 'react';
import { Tooltip } from 'antd';
import { AlertCircle, CheckCircle, AlertTriangle } from 'lucide-react';

export interface IssueCount {
  /** Distinct fields with at least one issue (deduped per field). */
  total: number;
  /** Subset of `total` whose issues are block-severity. */
  blocking: number;
  /** `total - blocking`. */
  review: number;
}

export interface IssueCountBadgeProps {
  /** Precomputed issue count from the list payload. `null`/`undefined` = not computable. */
  count?: IssueCount | null;
  /** When true, renders as a compact dot/icon+count (for table cells). Defaults to false. */
  compact?: boolean;
  /** Optional click handler (e.g. open the editor's issue panel). */
  onClick?: (e: React.MouseEvent) => void;
}

type IssueTier = 'clean' | 'blocking' | 'review';

function deriveTier(count: IssueCount | null | undefined): IssueTier | null {
  if (!count) return null;
  if (count.total <= 0) return 'clean';
  if (count.blocking > 0) return 'blocking';
  return 'review';
}

function buildTooltip(count: IssueCount): string {
  if (count.total === 0) return 'No issues';
  const issuePlural = count.total === 1 ? 'issue' : 'issues';
  if (count.blocking > 0) {
    const blockPlural = count.blocking === 1 ? 'blocking' : 'blocking';
    return `${count.total} ${issuePlural} · ${count.blocking} ${blockPlural}`;
  }
  return `${count.total} ${issuePlural} · review only`;
}

const COMPACT_ICON_SIZE = 14;

const COMPACT_PILL_STYLE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '1px 7px',
  borderRadius: 10,
  fontSize: 11,
  fontWeight: 600,
  lineHeight: '16px',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
};

const FULL_PILL_STYLE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '2px 9px',
  borderRadius: 11,
  fontSize: 11.5,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
};

const TIER_TOKENS: Record<
  Exclude<IssueTier, 'clean'>,
  { bg: string; fg: string; border: string; icon: typeof AlertCircle }
> = {
  blocking: {
    bg: 'var(--color-coral-light)',
    fg: 'var(--color-coral-dark)',
    border: '1px solid var(--color-coral)',
    icon: AlertCircle,
  },
  review: {
    bg: 'var(--color-amber-light)',
    fg: 'var(--color-amber-dark)',
    border: '1px solid var(--color-amber)',
    icon: AlertTriangle,
  },
};

export const IssueCountBadge: React.FC<IssueCountBadgeProps> = ({
  count,
  compact = false,
  onClick,
}) => {
  const tier = deriveTier(count);

  // Count not computable on the server side — render nothing.
  if (tier === null) return null;

  // Clean: calm teal check, no count text.
  if (tier === 'clean') {
    const tooltip = 'No issues';
    if (compact) {
      return (
        <Tooltip title={tooltip}>
          <span
            className="inline-flex items-center cursor-pointer"
            onClick={onClick}
            aria-label="No issues"
          >
            <CheckCircle
              size={COMPACT_ICON_SIZE}
              style={{ color: 'var(--color-teal-dark)' }}
            />
          </span>
        </Tooltip>
      );
    }
    return (
      <Tooltip title={tooltip}>
        <span
          style={{
            ...FULL_PILL_STYLE,
            background: 'var(--color-teal-light)',
            color: 'var(--color-teal-dark)',
            border: '1px solid var(--color-teal)',
          }}
          onClick={onClick}
          aria-label="No issues"
        >
          <CheckCircle size={12} style={{ color: 'var(--color-teal-dark)' }} />
          Clean
        </span>
      </Tooltip>
    );
  }

  // Has issues — render the count in a colored pill.
  const total = count?.total ?? 0;
  const tooltip = count ? buildTooltip(count) : '';
  const tokens = TIER_TOKENS[tier];
  const Icon = tokens.icon;

  if (compact) {
    return (
      <Tooltip title={tooltip}>
        <span
          style={{
            ...COMPACT_PILL_STYLE,
            background: tokens.bg,
            color: tokens.fg,
            border: tokens.border,
          }}
          onClick={onClick}
          aria-label={tooltip}
        >
          <Icon size={11} style={{ color: tokens.fg }} />
          {total}
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltip}>
      <span
        style={{
          ...FULL_PILL_STYLE,
          background: tokens.bg,
          color: tokens.fg,
          border: tokens.border,
        }}
        onClick={onClick}
        aria-label={tooltip}
      >
        <Icon size={12} style={{ color: tokens.fg }} />
        {total} {total === 1 ? 'issue' : 'issues'}
      </span>
    </Tooltip>
  );
};

export default IssueCountBadge;
